/**
 * Shared types for the Preview workspace components.
 * For Media Devoted | Created By Ciro Autiero
 */

import type {
  FlowLayer,
  FlowOptimizerItemPreview,
  RuleAction,
} from '@trafficpulse/shared';

/**
 * Minimal shape consumed by the read-only preview rows. Built from the
 * server's `FlowOptimizerItemPreview` for one item.
 */
export interface PreviewBreakdownContribution {
  metric: string;
  /** Slider weight in [0, 100]. */
  weight: number;
  /** Raw metric value from the cube. */
  value: number;
  /** value * (weight / 100). */
  contribution: number;
}

/** Per-sub-formula attribution for blocks-mode groups. */
export interface PreviewSubformulaContribution {
  subformulaId: string;
  name: string;
  expression: string;
  /** Evaluated value of the sub-formula (before weight scaling). */
  value: number;
  /** Normalized weight (%) within the group; omitted when unweighted. */
  weight?: number;
  /** value * (weight / 100) when weighted, else the raw value. */
  contribution: number;
}

export interface PreviewBreakdownEntry {
  groupId: string;
  groupName: string;
  rawScore: number;
  weightedScore: number;
  groupWeight: number;
  dateRange: string;
  /** Canonical text form of the expression that was evaluated. */
  expression?: string;
  /** Metric values the formula evaluated against. Empty when no traffic. */
  metrics?: Record<string, number>;
  /**
   * Per-metric contribution attribution, populated only when the formula
   * group is in `sliders` mode. Lets the hover card show "profit 70% × 100 = 70"
   * style breakdowns.
   */
  contributions?: PreviewBreakdownContribution[];
  /**
   * Per-sub-formula attribution for `blocks` mode — each sub-formula's value,
   * weight, and contribution. Lets the hover show the real per-sub math.
   */
  subformulaContributions?: PreviewSubformulaContribution[];
}

export interface PreviewRow {
  layer: FlowLayer;
  itemId: string;
  itemName: string;
  parentId?: string;
  pathId?: string;
  /** Bank row id (paths only) — lets the score popup name the groups skipping it. */
  pathBankId?: string;
  active: boolean;
  excluded: boolean;
  /**
   * Excluded by a rule → PAUSED: disabled on apply (weight value kept) and
   * removed from the budget pool, so the surviving items share the full 100%.
   */
  ruleExcluded?: boolean;
  /** Rule actions matching this item → per-action icons (exclude / set-to-queue
   *  / eliminate / add-element). Absent when no rule matches. */
  ruleActions?: RuleAction[];
  /** Weight rule pinning (static) or boosting (dynamic) this item — the pin
   *  glyph beside the next weight and the hover's explanation read this. */
  weightRule?: FlowOptimizerItemPreview['weightRule'];
  passThrough: boolean;
  currentWeight: number;
  currentPerMille: number;
  optimalWeight?: number;
  optimalPerMille: number;
  deltaPerMille: number;
  rawScore?: number;
  scoreShare?: number;
  reason?: string;
  breakdown?: PreviewBreakdownEntry[];
  /**
   * Raw stat-field values keyed by cube date-range, mirroring
   * `FlowOptimizerItemPreview.metricsByDateRange`. Optional because legacy
   * (cached) preview responses may not carry it yet.
   */
  metricsByDateRange?: Record<string, Record<string, number>>;
  /**
   * DISPLAY-only per-lander variants for OFFER rows, keyed by landerId. An offer
   * is weighted per-path, but its stats differ per lander it sits under — this
   * carries the per-(path,lander) score + Performance so the popup can show the
   * offer's metrics within the selected lander. The weight stays per-path.
   */
  perLander?: Record<
    string,
    {
      rawScore: number;
      breakdown?: PreviewBreakdownEntry[];
      metricsByDateRange?: Record<string, Record<string, number>>;
    }
  >;
}

export function toPreviewRow(item: FlowOptimizerItemPreview): PreviewRow {
  return {
    layer: item.layer,
    itemId: item.itemId,
    itemName: item.itemName,
    parentId: item.parentId,
    pathId: item.pathId,
    pathBankId: item.pathBankId,
    active: item.active,
    excluded: item.excluded,
    ruleExcluded: item.ruleExcluded,
    ruleActions: item.ruleActions,
    weightRule: item.weightRule,
    passThrough: item.passThrough,
    currentWeight: item.currentWeight,
    currentPerMille: item.currentPerMille,
    optimalWeight: item.optimalWeight,
    optimalPerMille: item.optimalPerMille,
    deltaPerMille: item.deltaPerMille,
    rawScore: item.rawScore,
    scoreShare: item.scoreShare,
    reason: item.reason,
    breakdown: item.breakdown,
    metricsByDateRange: item.metricsByDateRange,
    perLander: item.perLander,
  };
}

/**
 * Scope an offer preview row to a selected lander: when the row carries a
 * per-(path,lander) variant for `landerId`, swap in its score + breakdown +
 * Performance so the popup shows the offer's stats WITHIN that lander. The
 * weight fields are left untouched (offers are weighted per-path). Returns the
 * row unchanged for non-offers, no lander, or when no variant exists.
 */
export function scopePreviewRowToLander(
  row: PreviewRow | null,
  landerId: string | undefined,
): PreviewRow | null {
  if (!row || !landerId || row.layer !== 'offers') return row;
  const variant = row.perLander?.[landerId];
  if (!variant) return row;
  return {
    ...row,
    rawScore: variant.rawScore,
    breakdown: variant.breakdown,
    metricsByDateRange: variant.metricsByDateRange,
  };
}

/**
 * Index preview items by layer + itemId + parentId. Landers/offers can
 * appear under multiple paths, so parentId is part of the key.
 */
export type PreviewIndex = Map<string, PreviewRow>;

export function buildPreviewIndex(items: FlowOptimizerItemPreview[]): PreviewIndex {
  const out: PreviewIndex = new Map();
  for (const item of items) {
    const row = toPreviewRow(item);
    out.set(previewKey(item.layer, item.itemId, item.parentId), row);
    // Path lookups don't need parentId disambiguation — path ids are already
    // unique within a flow (the rotation→flow mapper prefixes rule-group
    // paths). Index a parent-less alias so callers can omit it.
    if (item.layer === 'paths') {
      out.set(previewKey(item.layer, item.itemId), row);
    }
    // Path-item rows look up offers by path id, but the walker keys them by
    // lander id when the path has landers. Alias by pathId for UI resolution.
    if (item.layer === 'offers' && item.pathId) {
      setOfferPathAlias(out, item.pathId, item.itemId, row);
    }
  }
  return out;
}

function setOfferPathAlias(
  index: PreviewIndex,
  pathId: string,
  itemId: string,
  row: PreviewRow,
): void {
  const key = previewKey('offers', itemId, pathId);
  const existing = index.get(key);
  if (!existing || preferPreviewRow(row, existing)) index.set(key, row);
}

function preferPreviewRow(next: PreviewRow, current: PreviewRow): boolean {
  if (next.active !== current.active) return next.active;
  return Math.abs(next.deltaPerMille) > Math.abs(current.deltaPerMille);
}

export function previewKey(layer: FlowLayer, itemId: string, parentId?: string): string {
  return `${layer}|${itemId}|${parentId ?? ''}`;
}

/** Show the excluded badge only when the item is on and matched by exclusion rules. */
export function showExcludedBadge(
  previewRow: PreviewRow | null | undefined,
  active: boolean,
): boolean {
  return Boolean(previewRow?.excluded && active);
}

/** Hover text for the excluded indicator — why the item is left out + the matched condition. */
export function excludedReasonText(
  previewRow: PreviewRow | null | undefined,
): string {
  const base = previewRow?.ruleExcluded
    ? 'Excluded by rule — kept at its current weight and ignored in the optimization'
    : 'Excluded — kept at its current weight and left out of the optimization';
  return previewRow?.reason ? `${base}. Matched: ${previewRow.reason}` : base;
}
